import { Component, EventEmitter, Input, Output, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OrganizationService } from 'src/app/data/service/organization.service';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { InvoiceUnReconciledAdd } from 'src/app/modules/bookkeeping/models/invoice/invoice-short';
import { TaxBasis } from 'src/app/modules/bookkeeping/enums/taxBasis';
import { InvoiceService } from 'src/app/data/service/invoice.service';
import { ToasterMessageService } from '../../toaster-message/toaster-message.service';
import { take } from 'rxjs/operators';
import { ToasterTypes } from 'src/app/core/enum/toaster-type.enum';
import { RecognizedInvoiceService } from 'src/app/modules/bookkeeping/services/recognized-invoice.service';
import { PaymentAccount } from 'src/app/modules/bookkeeping/models/account/payment-account';
import { Guid } from 'guid-typescript';
import { CurrencyCode } from 'src/app/modules/bookkeeping/enums/currency-code';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { NewSupplier } from 'src/app/modules/bookkeeping/models/supplier/new-supplier';
import { SupplierShort } from 'src/app/modules/bookkeeping/models/supplier/supplier-short';
import { SupplierWithAlias } from 'src/app/modules/bookkeeping/models/supplier/supplier-with-alias';
import { BankAccount } from 'src/app/modules/bookkeeping/models/account/bank-account';
import { Subscription } from 'rxjs';
import { AccountingIntegration } from 'src/app/data/enums/accounting-integration';
import { BaseComponent } from 'src/app/core/components/base/base.component';
import { SuppliersService } from 'src/app/data/service/suppliers.service';
import { BankAccountService } from 'src/app/data/service/bank-account.service';
import * as $ from 'jquery';
import { TaxRate } from 'src/app/modules/bookkeeping/models/taxRate';
import { QBOClass } from 'src/app/modules/bookkeeping/models/qbo-class';

@Component({
  selector: 'app-expense-modal',
  templateUrl: './expense-modal.component.html',
  styleUrls: ['./expense-modal.component.scss'],
  providers: [
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' }
  ],
  encapsulation: ViewEncapsulation.Emulated
})
export class ExpenseModalComponent extends BaseComponent implements OnInit {
  @Output() onClose = new EventEmitter<any>();
  @Input() amount: number | null = null;
  @Input() date: string | null = null;
  @Input() relatedID: string | null = null;
  @Input() invoiceId: string | null = null;
  @Input() accountId: string | null = null;
  @Input() invoiceColorNumber: number = 1;
  @Input() isPaperwork: boolean = false;

  /**
   * Bulk-coding mode. When `true` the modal hides amount / date / paid-from
   * fields (the BE derives those from each transaction itself) and on submit
   * posts a single bulk request covering all `bulkTransactionIds`. The single-
   * row flow is unaffected when this is left at its default `false`.
   */
  @Input() isBulkMode: boolean = false;
  @Input() bulkTransactionIds: string[] = [];

  /**
   * Emits the prepared bulk-code payload + shared block. The parent owns the
   * actual HTTP call so the modal can close immediately and the user can keep
   * working while coding runs in the background.
   */
  @Output() onBulkSubmit = new EventEmitter<{ payload: any; shared: any }>();

  expenseForm: FormGroup;
  isAmountEditable: boolean = true;
  isDateEditable: boolean = true;
  dateFormat: string;
  bankAccountList: PaymentAccount[] = [];

  reconciledDataAdd: InvoiceUnReconciledAdd;
  selectedSupplierId: string;
  organizationTaxRateId: string;
  orgTaxBasis: TaxBasis = TaxBasis.DefaultRate;
  description: string = '';
  isSaving: boolean = false;
  showInvoiceFormPanel: boolean = false;
  invoiceEditable: boolean = false;
  showExpenseModal: boolean = false;
  defaultCurrency: CurrencyCode;
  account: PaymentAccount = new PaymentAccount();

  isNewSupplierLoaded: boolean = false;
  selectedSupplierName: string;
  isNewSupplier: boolean = false;
  newSupplier: NewSupplier = new NewSupplier(
    this.organizationService.selectedOrganization
  );
  creatingNewSupplier: boolean = false;
  isCreateSupplierItemShown: boolean = false;
  suppliers: SupplierShort[] = [];
  currentSupplier: SupplierWithAlias = new SupplierWithAlias();
  bankAccount: BankAccount = new BankAccount(
    this.organizationService.selectedOrganization
  );
  subscriptions: Subscription[] = [];
  IsQuickBook: boolean = false;
  IsNoAccounting: boolean = false;
  accounts: PaymentAccount[] = [];
  supplierSearchList: SupplierShort[] = [];
  selectedSupplier: string = "";
  paymentAccountNumber: string = "";
  MyBankAccount: any;
  isFileUploadProcessing: boolean = false;
  taxRates: TaxRate[] = [];
  qboClasses: QBOClass[] = [];
  selectedQBOClassId: string = null;

  @ViewChild('addDocumentModel') addDocumentModel: ModalDirective;
  get expenseFormControl() {
    return this.expenseForm.controls;
  }

  constructor(
    private fb: FormBuilder,
    private organizationService: OrganizationService,
    private _adapter: DateAdapter<any>,
    private readonly _invoiceService: InvoiceService,
    private readonly _toasterMessageService: ToasterMessageService,
    private readonly _supplierService: SuppliersService,
    private _recognizedInvoiceService: RecognizedInvoiceService,
    private _bankAccountService: BankAccountService,
  ) {
    super();
    this.expenseForm = this.fb.group({
      supplier: new FormControl("", Validators.required),
      account: new FormControl('', Validators.required),
      date: new FormControl('', Validators.required),
      amount: new FormControl('', [Validators.required, Validators.min(0)]),
      details: new FormControl('', Validators.required),
      paymentAccountNumber: new FormControl('', Validators.required),
      selectedTaxRate: new FormControl('', Validators.required)
    });
    this.dateFormat = this.organizationService.selectedOrganizationDateFormat;
    if (this.dateFormat) {
      this._adapter.setLocale(this.dateFormat);
    }
  }

  onAmountInput(event: Event) {
    const input = event.target as HTMLInputElement;
    let value = input.value;

    // Remove any non-numeric characters except decimal point
    value = value.replace(/[^\d.]/g, '');

    // Ensure only one decimal point
    const parts = value.split('.');
    if (parts.length > 2) {
      value = parts[0] + '.' + parts.slice(1).join('');
    }

    // Limit to 2 decimal places
    if (parts.length === 2 && parts[1].length > 2) {
      value = parts[0] + '.' + parts[1].substring(0, 2);
    }

    // Update the input value
    if (value !== input.value) {
      input.value = value;
      this.expenseForm.get('amount').setValue(value);
    }
  }

  ngOnInit() {

    this.setIsQuickBookOrNot();
    this.GetBankAcoountDetails();
    this.getTaxRates();
    this.getOrganizationDefaultCurrency();
    this.getAccounts();
    this.getSuppliers();
    this.getQBOClasses();

    if (this.isBulkMode) {
      // In bulk mode the BE derives amount, date and bank account from each
      // selected transaction — strip the validators so the form can submit
      // without those values.
      this.expenseForm.get('amount').clearValidators();
      this.expenseForm.get('amount').updateValueAndValidity();
      this.expenseForm.get('date').clearValidators();
      this.expenseForm.get('date').updateValueAndValidity();
      this.expenseForm.get('paymentAccountNumber').clearValidators();
      this.expenseForm.get('paymentAccountNumber').updateValueAndValidity();
      return;
    }

    // Set default date to today if no date provided
    const today = new Date();
    const formValues: any = {
      date: this.date ? new Date(this.date) : today,
      paymentAccountNumber: this.isPaperwork ? this.accountId : null
    };

    // Set amount if provided, ensure it's positive
    if (this.amount !== null) {
      formValues.amount = Number(Math.abs(this.amount)).toFixed(2);
      this.isAmountEditable = false;
    }

    // Set date editability based on whether it was provided
    this.isDateEditable = !this.date;

    this.expenseForm.patchValue(formValues);
  }

  setIsQuickBookOrNot() {
    this.subscriptions.push(
      this.organizationService.organizationList.subscribe((data) => {
        const org = data?.find(
          (o) =>
            o.organisationID === this.organizationService.selectedOrganization
        );
        this.IsQuickBook =
          org?.accountingIntegration === AccountingIntegration.QBO
            ? true
            : false;
        this.IsNoAccounting =
          org?.accountingIntegration === AccountingIntegration.None
            ? true
            : false;
      })
    );
  }

  GetBankAcoountDetails() {
    this.organizationService
      .GetBankAccountDetails(
        'Organisation/GetBankAccountDetails',
        this.organizationService.selectedOrganization
      )
      .subscribe(
        (response: any) => {
          if (response.status == 0) {
            this.bankAccountList = response.data;
            if (this.bankAccountList?.length > 0) {
              this.bankAccountList.forEach((payAcc) => {
                payAcc.accountID = payAcc.accountID.toLowerCase();
              });
            }
          }
        },
        (err) => {
          this.bankAccountList = [];
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            err.message
          );
        }
      );
  }

  getOrganizationDefaultCurrency() {
    this.organizationService
      .getOrganizationDefaultCurrency(
        this.organizationService.selectedOrganization
      )
      .pipe(take(1))
      .subscribe(
        (response) => {
          this.defaultCurrency = response.currency;
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  getSelectedTaxRate() {
    this.organizationService
      .getOrganizationTaxRateId(this.organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (response) => {
          if (
            (this.IsQuickBook || this.IsNoAccounting) &&
            response.taxBasis == TaxBasis.IgnoreTax
          ) {
            this.orgTaxBasis = response.taxBasis;
            this.organizationTaxRateId = '0';
          } else {
            this.organizationTaxRateId = response.taxRateId;
            this.expenseForm.patchValue({ selectedTaxRate: this.organizationTaxRateId })
          }

        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  onSelectPaymentAccount(e) {
    this.MyBankAccount = this.bankAccountList.find(x => x.accountID === e.accountID);
    this.paymentAccountNumber = e.accountID;

    this.expenseForm.patchValue({ paymentAccountNumber: e.accountID });
  }

  getAccounts() {
    this._bankAccountService
      .getPaymentAccounts(this.organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe((accounts: PaymentAccount[]) => {
        this.accounts = accounts;
        // !!this.invoice?.invoiceLines[0]?.accountId
        //   ? (this.account = this.accounts.find(
        //       (a) =>
        //         a.id.toLowerCase() ===
        //         this.invoice.invoiceLines[0].accountId.toLowerCase()
        //     ))
        //   : this.updateAccount();
      });
  }

  updateAccount() {
    const code = this.suppliers.find(
      (s) => s.id === this.expenseForm.value.supplier
    )?.defaultAccountCode;
    let accountFind = this.accounts.find((x) => x.code == code);
    if (accountFind) {
      this.account = accountFind as PaymentAccount;
    } else {
      this.account = new PaymentAccount();
    }

    if (this.account?.name == '') {
      this.account.name = null;
    }
  }

  getSuppliers() {
    this._supplierService
      .getSuppliers(this.organizationService.selectedOrganization)
      .pipe(take(1))
      .subscribe((suppliers: SupplierShort[]) => {
        this.suppliers =
          suppliers?.sort((a, b) => a.name.localeCompare(b.name)) || [];
        this.suppliers.forEach((x) => (x.name = this.stripHTML(x.name)));
        this.supplierSearchList = this.suppliers;
      });
  }

  onSelectAccount(event: PaymentAccount) {
    if (event == undefined) {
      let data = {
        code: null,
        id: null,
        name: null,
        accountID: null,
      };
      this.account = data;
    }
    let data = {
      code: event.code,
      id: event.id,
      name: event.name,
      accountID: event.id,
    };
    this.account = data;
  }

  onSelectTaxRates(event: TaxRate) {
    this.organizationTaxRateId = event.id;
  }

  getTaxRates() {
    this.organizationService
      .getTaxRates(this.organizationService.selectedOrganization)
      .pipe(this.untilThis)
      .subscribe(
        (taxRates: TaxRate[]) => {
          this.taxRates = taxRates;
          this.getSelectedTaxRate();
        },
        (e: Error) => {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            'Error',
            e.message
          );
        }
      );
  }

  stripHTML(HTMLText: string): string {
    const reg = /<[^>]+>/gi;
    return HTMLText.replace(reg, '');
  }

  getQBOClasses() {
    const orgId = this.organizationService.selectedOrganization;
    if (!orgId) { return; }
    this.organizationService.getOrganizationQBOClasses(orgId)
      .pipe(this.untilThis)
      .subscribe(
        (classes) => { this.qboClasses = classes || []; },
        () => { this.qboClasses = []; }
      );
  }

  onSelectSupplier(event: SupplierShort) {
    this.isNewSupplierLoaded = true;
    if (event == undefined) {
      this.isNewSupplier = true;
      return;
    }
    this.isNewSupplier = false;
    this.isCreateSupplierItemShown = this.isNewSupplier;
    this.selectedSupplierName = event.name;
    this.getBankAccountDetailsBySupplierId(event.id, this.selectedSupplierName);
    this.selectedSupplierId = event.id;
  }

  getBankAccountDetailsBySupplierId(supplierId: string, supplieName: string) {
    this._bankAccountService
      .getBankAccountBySupplierId(
        supplierId,
        this.organizationService.selectedOrganization
      )
      .pipe(take(1))
      .subscribe((data) => {
        if (!!data) {
          Object.keys(data).forEach(
            (k) =>
              (this.bankAccount[k] = !!data[k] ? data[k] : this.bankAccount[k])
          );
          this.bankAccount.contactId = supplierId;
          this.bankAccount.accountName = supplieName;
        } else {
        }
      });
  }

  handleSupplierName(event) {
    this.newSupplier.name = event;
    const matchedSupplier = this.suppliers?.filter(
      (s) => s.name == this.newSupplier.name
    );

    if (matchedSupplier?.length == 0 && event) {
      this.isCreateSupplierItemShown = true;
    } else if (!event) {
      this.handleSupplierBlur();
    } else {
      this.isCreateSupplierItemShown = false;
    }
  }

  handleSupplierBlur() {
    this.isCreateSupplierItemShown = this.isNewSupplier;
  }

  createNewSupplier() {
    if (!this.creatingNewSupplier) {
      this.creatingNewSupplier = true;
      this.addNewSupplier(this.newSupplier, false);
    }
  }

  addNewSupplier(
    supplier: NewSupplier,
    setInvoiceSupplierName: boolean = true
  ) {
    supplier.bankAccountRequestModel = null;
    supplier.name = setInvoiceSupplierName
      ? this.expenseForm.value.supplier
      : supplier.name;
    const name = JSON.parse(JSON.stringify(supplier.name));
    const newSupplier = JSON.parse(JSON.stringify(supplier));
    newSupplier.bsb = newSupplier.bsb?.replace('-', '');
    newSupplier.currency = this.defaultCurrency;
    newSupplier.IsSupplier = true;
    newSupplier.OrganizationId = this.organizationService.selectedOrganization;
    newSupplier.hexColorClass = 'sup_img_box_' + this.invoiceColorNumber;
    if (!newSupplier.defaultAccount.id) newSupplier.defaultAccount = null;
    this.creatingNewSupplier = true;
    this._supplierService
      .createSupplier(newSupplier)
      .pipe(this.untilThis)
      .subscribe((result) => {
        if (result?.supplierId == 'ConnectionLost') {
          if (this.IsQuickBook) {
            this.organizationService.getIntegrationCredentials('qbo').subscribe((res) => {
              if (res) {
                this.organizationService.refreshConnectionURL = res.revokeEndPoint;
                this._toasterMessageService.displayToasterState(
                  ToasterTypes.ErrorWithHtml,
                  null,
                  `QuickBooks connection lost. Please <a  class=\"toastr-link\" href=\"${this.organizationService.refreshConnectionURL}\">refresh</a> connection`
                );
              }
            });
          } else {
            this.organizationService.getIntegrationCredentials('xero').subscribe((res) => {
              if (res) {
                this.organizationService.refreshConnectionURL = res.revokeEndPoint;
                this._toasterMessageService.displayToasterState(
                  ToasterTypes.ErrorWithHtml,
                  null,
                  `Xero connection lost. Please <a  class=\"toastr-link\" href=\"${this.organizationService.refreshConnectionURL}\">refresh</a> connection`
                );
              }
            });
          }
        } else if (result?.message !== undefined) {
          this._toasterMessageService.displayToasterState(
            ToasterTypes.Error,
            null,
            result?.message
          );
          this.isNewSupplier = false;
          this.creatingNewSupplier = false;
          this.isCreateSupplierItemShown = false;
          const refField = document.getElementById('refField');
          refField.focus();
        } else {
          this.suppliers = [
            ...this.suppliers,
            {
              id: result.supplierId,
              name: supplier.name,
              defaultAccountCode: supplier?.defaultAccount.code,
            },
          ];
          this.isNewSupplier = false;
          this.creatingNewSupplier = false;
          this.isCreateSupplierItemShown = false;
          const refField = document.getElementById('refField');
          refField.focus();
          this.account = supplier?.defaultAccount;
          if (supplier?.defaultAccount?.name == '') {
            supplier.defaultAccount.name = null;
          }
          $('.ng-dropdown-panel').remove();
          $('#ddlsupplier').removeClass('ng-select-opened');
        }
      });
  }

  close(event?: Event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault?.();
    }
    this.onClose.emit(null);
  }

  onSubmit() {
    if (!this.expenseForm.valid) return;

    if (this.isBulkMode) {
      this.submitBulkCoding();
      return;
    }

    const formValue = { ...this.expenseForm.value };
    // Ensure amount is formatted to 2 decimal places
    formValue.amount = Number(formValue.amount).toFixed(2);
    this.isFileUploadProcessing = true;
    this.updateCurrentMatchData();
  }

  /**
   * Build the bulk-code payload and hand it off to the parent. The parent
   * owns the HTTP call so the modal can close immediately and the user can
   * keep working — coding runs as a background job with row-level + chip
   * progress feedback driven from the parent.
   */
  private submitBulkCoding(): void {
    if (!this.bulkTransactionIds?.length) {
      this._toasterMessageService.displayToasterState(
        ToasterTypes.Warning,
        'No selection',
        'No transactions selected to code.'
      );
      return;
    }

    const formValue = this.expenseForm.value;
    const payload = {
      organisationId: this.organizationService.selectedOrganization,
      userId: Number(localStorage.getItem('UserId')),
      transactionIds: this.bulkTransactionIds,
      shared: {
        supplierId: this.selectedSupplierId,
        supplierName: formValue.supplier?.name ?? this.selectedSupplierName,
        accountId: this.account?.id,
        accountName: this.account?.name,
        details: formValue.details,
        currency: this.defaultCurrency,
        taxRateId: formValue.selectedTaxRate ?? this.organizationTaxRateId,
        taxBasis: this.orgTaxBasis,
        classId: this.selectedQBOClassId || null,
        costCentreId: null,
        jobs: [],
      },
    };

    this.onBulkSubmit.emit({ payload, shared: payload.shared });
    this.close();
  }

  uploadManualInvoice() {
    this.isSaving = true;
    this._invoiceService.uploadInvoiceManually(this.reconciledDataAdd);

    this._recognizedInvoiceService.invoiceState$.pipe(take(1)).subscribe(
      (invoice: any) => {
        this.relatedID = invoice.id;
        // this.relatedName = this.fileObj.file.name;
        var model = {
          unreconciledReportIds: this.invoiceId,
          invoiceId: this.relatedID,
          organizationId: this.organizationService.selectedOrganization,
        };
        this._invoiceService
          .updateSuggestedMatches(model)
          .pipe(take(1))
          .subscribe(() => {
            this.isSaving = false;
          });
      },
      (e: Error) => {
        this._toasterMessageService.displayToasterState(
          ToasterTypes.Error,
          'Error',
          (e as any)?.error?.errorMessage || e.message
        );
      }
    );
  }

  updateCurrentMatchData() {
    let currentPaymentFrom: any = this.bankAccountList.find(
      (x) => x.accountID == this.expenseForm.value.paymentAccountNumber
    );
    this.reconciledDataAdd = {
      id: Guid.create().toString(),
      invoiceNumber: 'SYNC-' + this.generateRandomNumber(),
      supplierName: this.expenseForm.value.supplier.name,
      date: new Date(this.expenseForm.value.date),
      amountDue: this.expenseForm.value.amount,
      accountName: this.account ? this.account.name : '',
      unRecDescription: this.expenseForm.value.description,
      unreconciledReportId: this.relatedID,
      type: 'ACCPAYCREDIT',
      supplierId: this.selectedSupplierId,
      accountId: this.account.id,
      currency: this.defaultCurrency,
      taxRateId: this.expenseForm.value.selectedTaxRateId ?? this.organizationTaxRateId,
      taxBasis: this.orgTaxBasis,
      required_Fields: true,
      organisationId: this.organizationService.selectedOrganization,
      bankAccount: {
        accountID: currentPaymentFrom?.accountID ?? null,
        name: currentPaymentFrom?.name ?? null,
        type:
          currentPaymentFrom?.type == 'CREDITCARD'
            ? 'BANK'
            : currentPaymentFrom?.type,
        currencyCode: this.defaultCurrency,
        bankAccountNumber: currentPaymentFrom?.bankAccountNumber ?? null,
      },
      isProcessed: false,
      description: this.expenseForm.value.details,
      isPaperwork: this.isPaperwork
    };
    (this.reconciledDataAdd as any).classId = this.selectedQBOClassId || null;
    this.onClose.emit(this.reconciledDataAdd);
  }

  generateRandomNumber(): number {
    return Math.floor(100000 + Math.random() * 900000);
  }
} 
